import {AccountInfo} from "@azure/msal-browser";
import {AuthenticatedTemplate, UnauthenticatedTemplate, useMsal} from "@azure/msal-react";
import {useGeneral} from "@contexts/GeneralContext.tsx";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import Logo from "@images/AppSymbol.png";
import {IToken} from "@models/IToken.ts";
import {ContentCopyRounded, Login, Logout, RefreshOutlined} from "@mui/icons-material";
import MenuIcon from '@mui/icons-material/Menu';
import {
    AppBar,
    Backdrop,
    Badge,
    Box,
    Divider,
    Drawer,
    Grid,
    IconButton,
    LinearProgress,
    Link,
    List,
    ListItem,
    Paper,
    Tooltip,
    Typography
} from "@mui/material";
import {BreadcrumbsMap} from '@shared/BreadcrumbsMap.tsx';
import {HealthCheckIcon} from "@shared/HealthCheckIcon.tsx";
import {LoadingAnythingProgressBar} from "@shared/LoadingAnythingProgressBar.tsx";
import {NotificationDrawer} from "@shared/NotificationDrawer.tsx";
import {NotificacoesButton} from "@shared/specialComponents/NotificacoesButton.tsx";
import {ThemeSelector} from "@shared/ThemeSelector.tsx";
import {Unauthenticated} from "@shared/Unauthenticated.tsx";
import React, {memo, PropsWithChildren, Suspense, useEffect, useMemo, useState} from "react";
import {Link as RouterLink, Outlet, useLocation, useNavigate} from "react-router";
import {useAuth} from "@contexts/hooks/UseAuth.tsx";
import {_Sitemap} from "@localization/siteMap/breadcrumbs/_Sitemap.ts";
import {PermissaoAplicacao} from "@models/seguranca/permissaoApp.ts";

export const DefaultLayout = () => {
    const [open, setOpen] = useState(false);
    const [activeAccount, setActiveAccount] = useState<AccountInfo | null>(null);
    const {instance, inProgress} = useMsal();
    const {hasAccess, permissoes, userData, refreshData} = useAuth()
    const {createAlert} = useAlert();
    const [notificacoesOpen, setNotificacoesOpen] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();

    const loading = ['login', 'ssoSilent', 'handleRedirect', 'logout'].includes(inProgress);

    useEffect(() => {
        if (instance.getActiveAccount() == null) {
            if (instance.getAllAccounts().length > 0) {
                instance.setActiveAccount(instance.getAllAccounts()[0]);
            }
        }
        setActiveAccount(instance.getActiveAccount());
    }, [instance, loading]);

    const podeAbrirApp = useMemo(() => {
        if (userData != null)
            return hasAccess(['AbrirAplicacao'])
        else return false
    }, [permissoes, userData])

    const toggleDrawer = (open: boolean) => (event: any) => {
        if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
            return;
        }
        setOpen(open);
    };

    const LoginHeader = () => {
        const {screenSizeLevel} = useGeneral();

        return activeAccount ?
            <IconButton loading={loading} color={'error'} onClick={() => instance.logout()}>
                <Typography hidden={screenSizeLevel > 8} color={'inherit'} fontSize={'small'}>
                    Sair ({activeAccount.name})
                </Typography>
                <Logout color={"inherit"}/>
            </IconButton> : <IconButton color={'success'}
                                        loading={loading}
                                        onClick={() => instance.loginRedirect()}>
                <Typography sx={{color: "inherit"}} fontSize={'small'}>
                    Entrar
                </Typography>
                <Login color={'inherit'}/>
            </IconButton>;
    };

    useEffect(() => {
        setOpen(false)
    }, [location.pathname]);

    return (
        <Suspense fallback={<Backdrop open={true}/>}>
            <Box>
                <DrawerMenu open={open} toggleDrawer={toggleDrawer}/>
                <Paper sx={{pb: '6vh'}}>
                    <AppBar position={'fixed'}>
                        {/*<Toolbar>*/}
                        <Grid container justifyContent={'space-between'}>
                            <Grid container spacing={1}>
                                <IconButton onClick={toggleDrawer(true)}>
                                    <MenuIcon/>
                                </IconButton>
                                <IconButton onClick={() => navigate('/')}>
                                    <img src={Logo} alt={''} style={{maxHeight: '30px'}}/>
                                </IconButton>
                                {import.meta.env.DEV &&
                                    <IconButton onClick={() => {
                                        navigator.clipboard.writeText('Bearer ' + (JSON.parse(sessionStorage.getItem('token') ?? '') as IToken).token).then(() => {
                                            createAlert('Token copiado para a área de transferência', 'info', 2000);
                                        });
                                    }}>
                                        <ContentCopyRounded/>
                                    </IconButton>}
                                <HealthCheckIcon/>
                                <AuthenticatedTemplate>
                                    <IconButton color={userData == null ? 'warning' : 'primary'}
                                                component={Tooltip}
                                                title={userData == null ? 'Problemas de autenticação, clique para atualizar' : 'Atualizar minhas permissoes'}>
                                        <Badge badgeContent={userData == null ?
                                            <Typography color={'error'}>!</Typography> : ''}>
                                            <RefreshOutlined color={userData == null ? 'warning' : 'primary'}
                                                             onClick={refreshData}/>
                                        </Badge>
                                    </IconButton>
                                </AuthenticatedTemplate>
                            </Grid>
                            <Grid container spacing={1}>
                                <NotificacoesButton setNotificacoesOpen={setNotificacoesOpen}
                                                    notificacoesOpen={notificacoesOpen}/>
                                <LoginHeader/>
                            </Grid>
                        </Grid>
                        {/*</Toolbar>*/}
                        <Grid size={12}>
                            <LoadingAnythingProgressBar/>
                        </Grid>
                    </AppBar>
                </Paper>
                <Box p={2}>
                    <BreadcrumbsMap/>
                    <AuthenticatedTemplate>
                        {podeAbrirApp && <Outlet/> || <Unauthenticated type={'Unauthorized'}/>}
                        <NotificationDrawer open={notificacoesOpen} setOpen={setNotificacoesOpen}/>
                    </AuthenticatedTemplate>
                    <UnauthenticatedTemplate>
                        <Unauthenticated/>
                    </UnauthenticatedTemplate>
                </Box>
            </Box>
            <Backdrop open={false}>
                <Box sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}}>
                    <Grid container justifyContent={'center'} alignItems={'center'} height={'100%'}>
                        <Grid size={12} sx={{backgroundColor: 'error'}}>
                            <Typography px={10}>
                                Carregando...
                            </Typography>
                            <LinearProgress/>
                        </Grid>
                    </Grid>
                </Box>
            </Backdrop>
        </Suspense>
    );
};

interface DrawerMenuProps {
    open: boolean;
    toggleDrawer: (open: boolean) => (e: any) => void;
}

const Opt = memo((props: PropsWithChildren<{ to: keyof typeof _Sitemap }>) => {
    const {hasAccess, permissoes} = useAuth()
    const endpoint = _Sitemap[props.to]
    const canAccess = useMemo(() => hasAccess(endpoint.permissao as PermissaoAplicacao[] | PermissaoAplicacao), [permissoes])
    const listProps = canAccess != false ? {
        component: RouterLink,
        to: endpoint.fullUrl
    } : {
        color: 'textDisabled'
    }

    return <ListItem>
        <Link {...listProps} >{props.children}</Link>
    </ListItem>
})

function DrawerMenu({open, toggleDrawer}: DrawerMenuProps) {
    const {accounts} = useMsal();
    const {userData, refreshData} = useAuth()

    const keys = Object.keys(_Sitemap)

    return (
        <Box sx={{display: 'flex'}}>
            <Drawer open={open} onClose={toggleDrawer(false)}>
                <Box
                    role="presentation"
                    onKeyDown={toggleDrawer(false)}
                >
                    <List>
                        <ListItem key={'text2'}>
                            Olá, {accounts.length > 0 && accounts[0].name}
                        </ListItem>
                        <ListItem>
                            <AuthenticatedTemplate>
                                <Typography>Atualizar minhas permissoes</Typography>
                                <IconButton color={userData == null ? 'warning' : 'info'} size={'small'}>
                                    <RefreshOutlined onClick={refreshData}/>
                                </IconButton>
                            </AuthenticatedTemplate>
                        </ListItem>
                    </List>
                    <Divider/>
                    <List>
                        <ListItem key={'text'}>
                            <ThemeSelector/>
                        </ListItem>
                    </List>
                    <Divider/>
                    {keys.map((key) => {
                        return <Opt key={key} to={key as keyof typeof _Sitemap}>{key}</Opt>
                    })}
                    <Divider/>
                </Box>
            </Drawer>
        </Box>
    );
}
